import React from 'react';
import { ArrowRight, Play, CheckCircle } from 'lucide-react';

const HeroSection = ({ onDemoClick, onWatchDemo, isDark }) => {
  const features = [
    'Voice AI Triage',
    'Real-time Documentation',
    'Clinical Intelligence',
    'Automated Orders',
    'Revenue Capture'
  ];

  return (
    <section 
      id="hero" 
      className={`relative min-h-screen flex items-center pt-20 transition-colors duration-300 ${
        isDark ? 'bg-slate-900' : 'bg-stone-50'
      }`}
      data-testid="hero-section"
    >
      {/* Subtle background pattern */}
      <div className={`absolute inset-0 pointer-events-none ${
        isDark 
          ? 'bg-gradient-to-b from-sky-900/10 via-transparent to-transparent' 
          : 'bg-gradient-to-b from-sky-50/50 via-transparent to-transparent'
      }`} />
      
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24 relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* Left Content - 60% */}
          <div className="lg:col-span-7">
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-8 ${
              isDark ? 'bg-sky-900/40 text-sky-300' : 'bg-sky-100 text-sky-700'
            }`}>
              <span className="w-2 h-2 bg-sky-500 rounded-full animate-pulse" />
              AI-Powered Clinical Workflow
            </div>

            <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight mb-8 tracking-tight ${
              isDark ? 'text-white' : 'text-slate-900'
            }`}>
              The Operating System for{' '}
              <span className="text-sky-500">Clinical Workflows</span>
            </h1>

            <p className={`text-lg md:text-xl mb-10 max-w-xl leading-relaxed ${
              isDark ? 'text-slate-400' : 'text-slate-600'
            }`}>
              SkyDoc connects voice, documentation, decision support, orders, follow-up, 
              and revenue into one continuous care workflow.
            </p>

            {/* Feature pills - minimal */}
            <div className="flex flex-wrap gap-2 mb-10">
              {features.map((feature) => (
                <span 
                  key={feature}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm ${
                    isDark 
                      ? 'bg-slate-800/80 text-slate-300' 
                      : 'bg-white text-slate-600 shadow-sm'
                  }`}
                >
                  <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                  {feature}
                </span>
              ))}
            </div>

            {/* CTAs - cleaner */}
            <div className="flex flex-wrap gap-4">
              <button
                onClick={onDemoClick}
                className="px-8 py-4 bg-sky-500 text-white font-medium rounded-full hover:bg-sky-600 transition-all duration-200 shadow-lg shadow-sky-500/25 inline-flex items-center gap-2"
                data-testid="hero-book-demo-btn"
              >
                Book Demo
                <ArrowRight className="w-5 h-5" />
              </button>
              <button
                onClick={onWatchDemo}
                className={`px-8 py-4 font-medium rounded-full transition-all duration-200 inline-flex items-center gap-2 ${
                  isDark 
                    ? 'bg-slate-800 text-slate-200 hover:bg-slate-700' 
                    : 'bg-white text-slate-700 hover:bg-stone-100 shadow-sm'
                }`}
                data-testid="hero-see-workflow-btn"
              >
                <Play className="w-5 h-5" />
                See Live Workflow
              </button>
            </div>

            {/* Trust indicators - minimal */}
            <div className={`mt-16 pt-8 border-t ${isDark ? 'border-slate-800' : 'border-stone-200'}`}>
              <div className={`flex flex-wrap items-center gap-8 text-sm ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                <span className="font-medium">NHS Ready</span>
                <span className="font-medium">HIPAA Compliant</span>
                <span className="font-medium">GDPR Certified</span>
              </div>
            </div>
          </div>

          {/* Right Content - 40% */}
          <div className="lg:col-span-5">
            <div className="relative">
              {/* Mock product UI - cleaner */}
              <div className={`rounded-2xl overflow-hidden ${
                isDark ? 'bg-slate-800 border border-slate-700' : 'bg-white shadow-xl shadow-slate-200/50'
              }`}>
                {/* Top bar */}
                <div className={`px-4 py-3 flex items-center gap-2 ${isDark ? 'bg-slate-900' : 'bg-slate-800'}`}>
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 bg-red-400 rounded-full" />
                    <div className="w-3 h-3 bg-amber-400 rounded-full" />
                    <div className="w-3 h-3 bg-emerald-400 rounded-full" />
                  </div>
                  <span className="ml-4 text-slate-500 text-sm font-mono">skydoc.ai/workspace</span>
                </div>
                
                {/* Content */}
                <div className="p-6">
                  {/* Status bar */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        isDark ? 'bg-sky-900/50' : 'bg-sky-100'
                      }`}>
                        <span className="text-sky-500 font-semibold text-sm">AI</span>
                      </div>
                      <div>
                        <p className={`font-medium ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>Active Consultation</p>
                        <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>Dr. Sarah Chen</p>
                      </div>
                    </div>
                    <span className="px-3 py-1 bg-emerald-500/10 text-emerald-500 rounded-full text-xs font-medium">
                      Live
                    </span>
                  </div>

                  {/* Transcript preview */}
                  <div className={`rounded-xl p-4 mb-4 ${isDark ? 'bg-slate-700/50' : 'bg-stone-50'}`}>
                    <p className={`text-xs mb-2 font-medium ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>Live Transcript</p>
                    <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                      "I've been experiencing chest tightness and a persistent cough for the past three days..."
                    </p>
                  </div>

                  {/* AI suggestions */}
                  <div className="space-y-3">
                    <p className={`text-xs font-medium ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>AI Suggestions</p>
                    <div className="flex flex-wrap gap-2">
                      <span className={`px-2.5 py-1 rounded-lg text-xs font-medium ${
                        isDark ? 'bg-sky-900/30 text-sky-400' : 'bg-sky-50 text-sky-700'
                      }`}>
                        J18.9 Pneumonia
                      </span>
                      <span className={`px-2.5 py-1 rounded-lg text-xs font-medium ${
                        isDark ? 'bg-amber-900/30 text-amber-400' : 'bg-amber-50 text-amber-700'
                      }`}>
                        Chest X-Ray
                      </span>
                      <span className={`px-2.5 py-1 rounded-lg text-xs font-medium ${
                        isDark ? 'bg-emerald-900/30 text-emerald-400' : 'bg-emerald-50 text-emerald-700'
                      }`}>
                        CBC Required
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating notification - cleaner */}
              <div className={`absolute -bottom-4 -left-4 rounded-xl p-4 max-w-xs animate-fade-in-up ${
                isDark ? 'bg-slate-800 border border-slate-700' : 'bg-white shadow-lg'
              }`}>
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    isDark ? 'bg-emerald-900/50' : 'bg-emerald-100'
                  }`}>
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                  </div>
                  <div>
                    <p className={`text-sm font-medium ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>Documentation Complete</p>
                    <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>SOAP note generated in 2.3s</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
